import { useState, useEffect, useMemo } from 'react';
import {
  ChakraBox,
  FAIconName,
  TagProps,
  useToastMessage,
} from '@equip.health/ui';
import {
  CarePlanGoalResponse,
  CarePlanProblemResponse,
} from '~/lib/types/carePlan/carePlans';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { useUpdateGoalMutation } from '~/store/services/carePlan';
import { useStickyExpandAllLabel } from '~/lib/hooks/carePlan/useStickyExpandAllLabel';
import { CarePlanGoalRow, type CarePlanGoal } from './CarePlanGoalRow';
import { CarePlanSectionHeader } from './CarePlanSectionHeader';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { CarePlanEvents } from '~/analytics/carePlanEvents';
import { CARE_PLAN_SECTION_IDS } from '~/lib/constants/carePlan.constants';

type Props = {
  sectionId: string;
  sectionTitle: string;
  problems: CarePlanProblemResponse[];
  treatmentEpisodeId: string;
  carePlanId: string;
  statusDropdownOptionsByProblemType?: Record<string, TagProps[]>;
  displayProblemLabels?: boolean;
  onEditProblemClick: (problem: CarePlanProblemResponse) => void;
  onEditGoalClick: (goal: CarePlanGoalResponse) => void;
  onAddInterventionClick?: (goal: CarePlanGoal) => void;
  onRemoveGoalClick: (problemId: string) => void;
};

export const CarePlanGoalsSection = ({
  sectionId,
  sectionTitle,
  problems,
  treatmentEpisodeId,
  carePlanId,
  statusDropdownOptionsByProblemType,
  displayProblemLabels = true,
  onEditProblemClick,
  onEditGoalClick,
  onAddInterventionClick,
  onRemoveGoalClick,
}: Props) => {
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [updateGoal, { error }] = useUpdateGoalMutation();
  const { createToast } = useToastMessage();
  const { trackEvent } = useAnalytics();

  useEffect(() => {
    if (error) {
      createToast({
        id: 'goal-card-update-error',
        icon: FAIconName.exclamationTriangleRegular,
        text: "An error occurred updating the goal's status. Please try again later.",
        variant: 'critical',
      });
    }
  }, [error, createToast]);

  const goals: CarePlanGoal[] = useMemo(
    () =>
      problems.flatMap((problem) =>
        problem.goals.map((goal) => ({
          id: goal.id,
          title: goal.pathwayGoal.label,
          description: goal.description,
          status: goal.status.code as CarePlanGoalStatus,
          statusLabel: goal.status.label,
          problemId: problem.id,
          problemType: problem.problemType,
          problem: problem,
          latestNote: goal.descriptionDetails
            ? {
                text: goal.descriptionDetails.description,
                timestamp: goal.descriptionDetails.effectiveDate,
                providerId: goal.descriptionDetails.changedBy,
              }
            : undefined,
        })),
      ),
    [problems],
  );

  const toggleGoal = (goalId: string) => {
    setExpandedGoals((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(goalId)) {
        newSet.delete(goalId);
      } else {
        newSet.add(goalId);
      }
      return newSet;
    });
  };

  const showsCollapseAll = useStickyExpandAllLabel(goals, expandedGoals);

  const handleExpandAll = () => {
    // Track expand/collapse based on section
    if (showsCollapseAll) {
      if (sectionId === CARE_PLAN_SECTION_IDS.TREATMENT_GOALS) {
        trackEvent(CarePlanEvents.clickOnCollapseAllTreatmentGoalsButton);
      } else if (sectionId === CARE_PLAN_SECTION_IDS.CO_OCCURRING) {
        trackEvent(CarePlanEvents.clickOnCollapseAllCoOccurringGoalsButton);
      }
      setExpandedGoals(new Set());
    } else {
      if (sectionId === CARE_PLAN_SECTION_IDS.TREATMENT_GOALS) {
        trackEvent(CarePlanEvents.clickOnExpandAllTreatmentGoalsButton);
      } else if (sectionId === CARE_PLAN_SECTION_IDS.CO_OCCURRING) {
        trackEvent(CarePlanEvents.clickOnExpandAllCoOccurringGoalsButton);
      }
      setExpandedGoals(new Set(goals.map((g) => g.id)));
    }
  };

  if (goals.length === 0) {
    return null;
  }

  return (
    // TODO: 30px padding is a temp fix between the Care Plan header and the Treatment Goals section.
    // The gap should be set to 42px on the Active Care Plan container. Now it's 12px + 30px = 42px.
    <ChakraBox paddingTop="30px" marginBottom="12px">
      <CarePlanSectionHeader
        id={sectionId}
        title={sectionTitle}
        count={goals.length}
        expandAllLabel={showsCollapseAll ? 'Collapse all' : 'Expand all'}
        onExpandAllClick={handleExpandAll}
      />
      <ChakraBox>
        {goals.map((goal) => (
          <CarePlanGoalRow
            key={goal.id}
            goal={goal}
            isExpanded={expandedGoals.has(goal.id)}
            carePlanId={carePlanId}
            treatmentEpisodeId={treatmentEpisodeId}
            statusDropdownOptions={
              // Sections that opt into per-problem-type statuses get an empty
              // menu for an unmapped type; sections that pass nothing (ED
              // treatment goals) keep CarePlanGoalRow's hardcoded default.
              statusDropdownOptionsByProblemType &&
              (statusDropdownOptionsByProblemType[goal.problemType] ?? [])
            }
            displayProblemLabels={displayProblemLabels}
            onToggle={toggleGoal}
            onEditProblemClick={onEditProblemClick}
            onEditGoalClick={onEditGoalClick}
            onAddInterventionClick={onAddInterventionClick}
            onRemoveGoalClick={onRemoveGoalClick}
            onStatusChange={(goalId, statusCode, description) => {
              updateGoal({
                treatmentEpisodeId,
                goalId,
                body: {
                  description,
                  statusCode,
                },
              });
            }}
          />
        ))}
      </ChakraBox>
    </ChakraBox>
  );
};
